/**
 * LE BON DE LIVRAISON — contrôle, scan, intégration (maquette `acBLOn`), et ses deux satellites :
 * « Corriger le stock » (acFix, D19) et « Nouveau BL » (acNb).
 *
 * ⚠️ L'ÉTAT VIENT DU SERVEUR, dérivé des lignes — l'écran ne le recalcule jamais.
 * ⚠️ DIVERGENCE ASSUMÉE (décision utilisateur D2) : là où le dessin bloque l'intégration sur « PA
 *    manquant », on INTÈGRE quand même et on DIT combien de pièces entrent sans prix.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { confirm, Icon, Modal, ScanBlock, showToast, Trans, useLatest } from '@orgazone/ui-web';
import { api, type DeliveryNoteDetail, type DeliveryNoteLine, type StockCorrectionReason } from '../api';
import { fieldLabel } from '../vocabulary';
import {
  dateIso, deliveryNoteSubtitle, Ecart, ETATS, euroFin, finDepuisSaisie, imprimerBonA4, libelleBon,
  lignesAEtiqueterDuBon, MONO, MOTIFS_CORRECTION, origineDuPrix, StatusPill, Source,
} from './receptions-shared';
import { EtiquettesModal } from './receptions-etiquettes';
import { t } from '../i18n';

/** Le gabarit de la table des lignes — RELEVÉ (maquette l.9377). */
const GRID = 'minmax(0,1.3fr) 50px 64px 128px 64px 70px 70px 148px';

type DernierScan = { txt: string; tone: 'ok' | 'warn' | 'err'; ask?: { gencode: string; name: string } };

/**
 * Les pièces contrôlées qui restent à entrer — ZÉRO sur un bon verrouillé (clôturé ou corrigé) : plus rien n'y entre.
 * ⚠️ Un bon clôturé avec reliquat peut garder des pièces contrôlées jamais entrées : les compter annonçait « prêtes à
 *    entrer » sur un bon qui n'en recevra plus, et le bouton « Fermer » tentait une intégration que le serveur refuse.
 */
function piecesAEntrer(bon: DeliveryNoteDetail): number {
  if (bon.resume.etat === 'closed' || bon.corrected) return 0;
  return bon.lines.filter((l) => l.known && l.checkedQty != null).reduce((n, l) => n + Math.max(0, l.checkedQty! - l.receivedQty), 0);
}

/** Pourquoi un BL d'une marque sans fournisseur n'entre pas en stock, et où la rattacher — la phrase du serveur. */
const awaitingText = (brand: string): string =>
  t('La marque {brand} n’est rattachée à aucun fournisseur : ce BL ne peut pas entrer en stock. Déclarez-la chez son fournisseur dans Référentiels → Fournisseurs, puis revenez l’intégrer.', { brand });

export function BonModal({ numero, onClose }: { numero: string; onClose: () => void }) {
  const [bon, setBon] = useState<DeliveryNoteDetail | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [scan, setScan] = useState('');
  const [dernier, setDernier] = useState<DernierScan | null>(null);
  const [dateEntree, setDateEntree] = useState<string>(dateIso(Date.now()));
  const [occupe, setOccupe] = useState(false);
  const [fix, setFix] = useState(false);
  const [etiquettes, setEtiquettes] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  // ⚠️ LE DERNIER GESTE LANCÉ ÉCRIT LE BON : la lecture d'ouverture, un contrôle, un scan et l'intégration rendent chacun
  //    le bon entier — une réponse revenue après un geste plus récent remettrait l'écran en arrière (un scan rapide
  //    effacé par la lecture d'ouverture). La même porte les ordonne tous.
  const latest = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latest();
    try {
      const b = await api.delivery(numero);
      if (isLatest()) { setBon(b); setFailure(null); }
    } catch (e) {
      if (isLatest()) setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [numero, latest]);
  useEffect(() => { void reload(); }, [reload]);

  // ⚠️ LE CURSEUR ATTEND DANS LA CASE DE SCAN dès l'ouverture (demande utilisateur 2026-08-28) : la
  //    douchette bipe sans toucher la souris. Deux minuteries de rattrapage — la modale pose son
  //    propre focus en s'ouvrant, on repasse APRÈS elle. Une seule fois par bon : les rechargements
  //    (chaque scan recharge) ne volent pas le focus là où l'opérateur tape.
  const numeroCharge = bon?.number ?? null;
  useEffect(() => {
    if (numeroCharge == null) return;
    const put = () => scanRef.current?.focus();
    put();
    const t1 = window.setTimeout(put, 60);
    const t2 = window.setTimeout(put, 180);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [numeroCharge]);

  /** Applique une écriture et relit le bon qu'elle rend — `false` si le serveur l'a refusée (le motif est dit). */
  const appliquer = useCallback(async (fn: () => Promise<DeliveryNoteDetail>): Promise<boolean> => {
    const isLatest = latest();
    try {
      const b = await fn();
      if (isLatest()) setBon(b);
      return true;
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); return false; }
  }, [latest]);

  const controler = useCallback((l: DeliveryNoteLine, checkedQty: number | null, unitPriceFine?: number | null) =>
    appliquer(() => api.checkDelivery(numero, [{ gencode: l.gencode, checkedQty, ...(unitPriceFine !== undefined ? { unitPriceFine } : {}) }])), [appliquer, numero]);

  /** Le scan qui écrit vraiment (+1 serveur), et le message qui raconte ce qui vient d'arriver. */
  const scanner = useCallback(async (code: string, ajoutConfirme: boolean) => {
    const isLatest = latest();
    try {
      const b = await api.scanDelivery(numero, code);
      if (isLatest()) setBon(b);
      const l = b.lines.find((x) => x.gencode === code);
      if (l?.known) {
        const nom = `${l.name ?? code}${l.size ? ` · ${l.size}` : ''}`;
        setDernier(ajoutConfirme
          ? { txt: t('+1 · {nom} — ajouté au BL, écart tracé (attendu {expectedQty} / reçu {v})', { nom, expectedQty: l.expectedQty, v: l.checkedQty ?? 0 }), tone: 'ok' }
          : { txt: l.expectedQty
            ? t('+1 · {nom} — contrôlé {v} / attendu {expectedQty}', { nom, v: l.checkedQty ?? 0, expectedQty: l.expectedQty })
            : t('+1 · {nom} — contrôlé {v}', { nom, v: l.checkedQty ?? 0 }), tone: 'ok' });
      } else {
        setDernier({ txt: t('{code} — inconnu au catalogue, exclu de l’intégration', { code }), tone: 'err' });
      }
    } catch (e) {
      setDernier({ txt: e instanceof Error ? e.message : String(e), tone: 'err' });
    }
    scanRef.current?.focus();
  }, [numero, latest]);

  const biper = useCallback(async () => {
    const code = scan.trim();
    if (!code || !bon) return;
    setScan('');
    const surLeBon = bon.lines.some((l) => l.gencode === code);
    // ⚠️ Un bon NÉ DU SCAN (aucune ligne attendue) prend tout directement : le scan EST le réel.
    //    Un bon qui ANNONCE des lignes (EDI, import, commande) demande avant d'ajouter un article
    //    hors bordereau — le geste de la maquette (« Ajouter au BL (+1) »).
    const neDuScan = bon.lines.every((l) => l.expectedQty === 0);
    if (surLeBon || neDuScan) { await scanner(code, false); return; }
    try {
      const r = await api.labelData([code]);
      const connu = r.rows[0];
      if (connu) {
        setDernier({ txt: t('{desig}{v} — connu au catalogue mais absent de ce BL', { desig: connu.desig, v: connu.size ? ` · ${connu.size}` : '' }), tone: 'warn', ask: { gencode: code, name: connu.desig } });
        scanRef.current?.focus();
        return;
      }
    } catch { /* ⚠️ le catalogue injoignable ne bloque pas le quai : le scan direct tranchera (ghost visible) */ }
    await scanner(code, false);
  }, [scan, bon, scanner]);

  const deleteLine = useCallback(async (gencode: string, nom: string, fantome: boolean) => {
    const ok = await confirm(fantome
      ? {
        title: t('Supprimer la ligne fantôme {gencode} ?', { gencode }),
        message: t('Le code disparaît de ce BL. Si la pièce est bien réelle, créez d’abord l’article au catalogue puis rescannez.'),
        confirmLabel: t('Supprimer'),
      }
      : {
        title: t('Supprimer la ligne « {nom} » ?', { nom }),
        message: t('Aucune pièce entrée en stock — la ligne disparaît de ce BL. Le document fournisseur, lui, ne change pas.'),
        confirmLabel: t('Supprimer la ligne'),
      });
    if (!ok) return;
    await appliquer(() => api.deleteDeliveryLine(numero, gencode));
  }, [appliquer, numero]);

  const integrer = useCallback(async () => {
    if (!bon || occupe) return;
    // ⚠️ RIEN À ENTRER — y compris sur un bon verrouillé —, le bouton dit « Fermer » et ferme.
    if (piecesAEntrer(bon) === 0) { onClose(); return; }
    // ⚠️ UNE MARQUE SANS FOURNISSEUR N'ENTRE PAS EN STOCK : le bandeau le dit déjà, le bouton ne fait pas semblant —
    //    le serveur le refuserait de toute façon, avec la même phrase.
    if (bon.awaitingBrand) { showToast(awaitingText(bon.awaitingBrand), 'warn'); return; }
    setOccupe(true);
    const isLatest = latest();
    try {
      const r = await api.integrateDelivery(numero, dateEntree ? new Date(`${dateEntree}T12:00:00`).getTime() : null);
      if (isLatest()) setBon(r.bon);
      // ⚠️ CE QUI EST ENTRÉ SANS PRIX SE DIT — D2 : on n'a rien bloqué, on ne cache rien non plus.
      showToast(r.piecesSansPrix > 0
        ? t('{n} pièce entrée en stock · {piecesSansPrix} sans prix d’achat, à valoriser', { n: r.pieces, piecesSansPrix: r.piecesSansPrix })
        : t('{n} pièce entrée en stock · {numero}', { n: r.pieces, numero }));
      // ⚠️ UN ARCHIVÉ QUI REVIENT SE DIT : il redescend en caisse, et personne ne saurait pourquoi sinon.
      if (r.restoredModels > 0) showToast(t('{n} modèle archivé remis en service : de la marchandise vient d’entrer pour lui.', { n: r.restoredModels }));
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setOccupe(false); }
  }, [bon, occupe, numero, dateEntree, onClose, latest]);

  const cloturer = useCallback(async () => {
    if (!bon) return;
    const ok = await confirm({
      title: t('Clôturer le BL {numero} avec reliquat ?', { numero }),
      message: t('{n} pièce annoncée n’est pas entrée et ne le sera plus sur ce bon. Le reliquat reste visible dans l’historique.', { n: bon.resume.expected - bon.resume.received }),
      confirmLabel: t('Clôturer avec reliquat'), tone: 'info',
    });
    if (!ok) return;
    await appliquer(() => api.closeDelivery(numero));
  }, [bon, numero, appliquer]);

  if (failure) {
    return (
      <Modal open onClose={onClose} title={t('BL {numero}', { numero })} width="md">
        <p role="alert" style={{ margin: 0, color: 'var(--danger-ink)', fontSize: '13px' }}>{t('Le bon n’a pas pu être lu : {failure}', { failure })}</p>
      </Modal>
    );
  }
  if (!bon) return <Modal open onClose={onClose} title={t('BL {numero}', { numero })} width="md"><p style={{ margin: 0, color: 'var(--ink-500)' }}>{t('Lecture…')}</p></Modal>;

  const libelle = libelleBon(bon.resume, bon.corrected);
  const verrouille = bon.resume.etat === 'closed' || bon.corrected;
  const scanOuvert = bon.resume.etat !== 'received' && !verrouille;
  const aEntrer = piecesAEntrer(bon);
  const sansPrix = aEntrer === 0 ? 0 : bon.lines.filter((l) => l.known && l.checkedQty != null && l.unitPriceFine == null).reduce((n, l) => n + Math.max(0, l.checkedQty! - l.receivedQty), 0);
  const tousControles = bon.resume.uncheckedLines === 0;

  // Le pied de la maquette (`acBLFoot`) : ce qui est prêt, ce qui manque, ce qui est fait — et un bon clôturé ne promet
  // plus rien.
  const pied = bon.resume.etat === 'closed' ? t('Clôturé avec reliquat — plus rien n’entre sur ce bon')
    : aEntrer > 0 && sansPrix > 0 ? t('{n} PA vide à compléter', { n: sansPrix })
      : aEntrer > 0 ? (tousControles ? t('{n} prête à entrer', { n: aEntrer }) : t('{n} prête · reste en attente', { n: aEntrer }))
        : tousControles && bon.resume.received > 0 ? t('Tout est entré') : t('Contrôlez les lignes reçues');

  // La note pédagogique de la maquette (`acBLNote`) — par SOURCE, pas par état.
  const note = bon.source === 'edi' || bon.source === 'import' || bon.source === 'legacy'
    ? t('Document déclaré par le fournisseur : le contrôle est recommandé avant d’intégrer. Corriger « Contrôlé » suffit — on entre en stock le réel, la commande gardera la trace de l’écart.')
    : t('Document né d’un scan : les quantités contrôlées SONT le réel — rien à ressaisir. Seuls les articles reconnus s’intègrent.');

  // Le premier code encore à contrôler — l'indice du champ de scan (maquette `acScanHint`).
  const suggestion = bon.lines.find((l) => l.known && (l.checkedQty == null || l.checkedQty < l.expectedQty))?.gencode;

  return (
    <>
    <Modal
      open onClose={onClose} title={bon.number} width="xxl" icon="inbound"
      subtitle={deliveryNoteSubtitle(bon)}
      meta={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><Source s={bon.source} /><StatusPill tone={ETATS[bon.resume.etat]} l={libelle} /></span>}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={() => imprimerBonA4(bon, libelle)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Icon name="printer" size={14} />  {t('Imprimer la liste (A4)')}
          </button>
          <button type="button" className="btn-ghost" onClick={() => setEtiquettes(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Icon name="barcode" size={15} />  {t('Étiquettes…')}
          </button>
          {bon.resume.etat === 'received' && (
            <button type="button" onClick={() => setFix(true)}
              style={{ height: 40, padding: '0 15px', border: '1px solid var(--amber)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--amber-fg)', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
            >{t('Corriger le stock…')}</button>
          )}
          {bon.resume.etat === 'partial' && (
            <button type="button" className="btn-ghost" onClick={() => void cloturer()}>{t('Clôturer avec reliquat')}</button>
          )}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '12px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{pied}</span>
          {aEntrer > 0 && (
            <label title={t('Date d’entrée en stock — antidatable pour un traitement en retard, jamais dans le futur')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Entré le')}</span>
              <input type="date" value={dateEntree} max={dateIso(Date.now())} onChange={(e) => setDateEntree(e.target.value)}
                style={{ border: 'none', background: 'transparent', fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', outline: 'none', fontFamily: 'inherit', width: 118 }} />
            </label>
          )}
          <button
            type="button" onClick={() => void integrer()} aria-disabled={occupe || undefined}
            style={{
              height: 40, padding: '0 16px', borderRadius: 'var(--r-sm)', fontSize: '13.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: occupe ? 'progress' : 'pointer',
              border: `1px solid ${aEntrer > 0 ? 'transparent' : 'var(--ink-300)'}`,
              background: aEntrer > 0 ? 'var(--ink-900)' : 'var(--paper)', color: aEntrer > 0 ? 'var(--paper)' : 'var(--ink-800)',
            }}
          >{aEntrer > 0 ? t('Entrer {n} pièce en stock', { n: aEntrer }) : t('Fermer')}</button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {bon.awaitingBrand && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '12.5px', fontWeight: 600 }}>
            <Icon name="alert" size={16} />
            <span style={{ flex: 1, minWidth: 0 }}>{awaitingText(bon.awaitingBrand)}</span>
          </div>
        )}
        {bon.parcels && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', border: '1px solid oklch(0.8 0.08 240)', fontSize: '12.5px', color: 'var(--ink-800)' }}>
            <Icon name="box" size={16} />
            <span style={{ flex: 1, minWidth: 0 }}><Trans k='<1>{parcels}</1> — on intègre ce qui est là, le reste attend le prochain colis sur ce même BL.' vars={{ parcels: bon.parcels }} parts={[(x) => <strong>{x}</strong>]} /></span>
          </div>
        )}

        {/* ⚠️ LE BLOC DE SCAN EST PARTAGÉ (règle de 2, 2026-09-03) : il était transposé à la main ici,
            et l'inventaire allait en poser une seconde copie. Deux copies, c'est une hauteur de champ
            qui change d'un côté, un ton d'alerte qui diverge, et surtout un GESTE qui ne se comporte
            plus pareil selon l'écran — or celui-là s'exécute la douchette en main, sans regarder. */}
        {scanOuvert && (
          <ScanBlock
            value={scan} onChange={setScan} onSubmit={() => void biper()}
            placeholder={t('Scannez ici — douchette ou saisie du code-barres, Entrée pour biper')}
            hint={suggestion ? t('Chaque bip = +1 · essayez : {suggestion}', { suggestion }) : t('Chaque bip = +1 — scannez un code du catalogue')}
            last={dernier ? {
              tone: dernier.tone,
              text: dernier.txt,
              action: dernier.ask
                ? { label: t('Ajouter au BL (+1)'), onClick: () => { const g = dernier.ask!.gencode; setDernier(null); void scanner(g, true); } }
                : undefined,
            } : null}
            focusOn={dernier}
          />
        )}

        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflowX: 'auto' }}>
          <div style={{ minWidth: 770 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <span>{t('Article')}</span><span style={{ textAlign: 'center' }}>{fieldLabel('size')}</span><span style={{ textAlign: 'right' }}>{t('Attendu')}</span>
              <span style={{ textAlign: 'center' }}>{t('Contrôlé')}</span><span style={{ textAlign: 'center' }}>{t('Écart')}</span>
              <span style={{ textAlign: 'right' }}>{t('Déjà entré')}</span><span style={{ textAlign: 'right' }}>{t('À entrer')}</span><span style={{ textAlign: 'right' }}>{t('PA retenu')}</span>
            </div>
            {bon.lines.length === 0 && (
              <div style={{ padding: '22px 14px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune ligne — scannez la première pièce.')}</div>
            )}
            {bon.lines.map((l) => l.known ? (
              <LigneConnue key={l.gencode} l={l} verrouille={verrouille} etat={bon.resume.etat}
                onControle={controler}
                onSupprime={() => void deleteLine(l.gencode, `${l.name ?? l.gencode}${l.size ? ` · ${l.size}` : ''}`, false)} />
            ) : (
              <div key={l.gencode} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', background: 'var(--rose-bg)', fontSize: '12.5px' }}>
                <Icon name="alert" size={15} color="var(--rose-fg)" />
                <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-800)' }}>
                  {/* ⚠️ UNE PHRASE, PAS QUATRE FRAGMENTS : écrite en morceaux (« — code-barres »,
                      « . Le BL »), elle arrive chez le traducteur dans un ordre imposé par le
                      français, qu’aucune langue ne peut changer. Les marqueurs numérotés portent la
                      mise en forme, le code ne porte que les valeurs. */}
                  <Trans
                    k='<1>Article inconnu</1> — code-barres <2>{gencode}</2> × {qte}. Le BL <3>reste ouvert</3> tant que cette ligne subsiste : créez l’article au catalogue, ou supprimez la ligne.'
                    vars={{ gencode: l.gencode, qte: l.checkedQty ?? 0 }}
                    parts={[
                      (x) => <strong style={{ color: 'var(--rose-fg)' }}>{x}</strong>,
                      (x) => <span style={{ fontFamily: MONO, fontSize: '11px' }}>{x}</span>,
                      (x) => <strong>{x}</strong>,
                    ]}
                  />
                </span>
                <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--paper)', color: 'var(--rose-fg)', fontSize: '10px', fontWeight: 800, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>{t('EXCLU')}</span>
                {!verrouille && (
                  <button type="button" aria-label={t('Supprimer la ligne fantôme {v}', { v: l.gencode })}
                    onClick={() => void deleteLine(l.gencode, l.gencode, true)}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}
                  ><Icon name="trash" size={13} /></button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.6 }}>{note}</div>
      </div>
    </Modal>
    {fix && (
      <CorrectionModal
        bon={bon}
        onClose={() => setFix(false)}
        onEcrit={(b) => { setFix(false); setBon(b); }}
      />
    )}
    {etiquettes && (
      <EtiquettesModal
        id={bon.number}
        lines={lignesAEtiqueterDuBon(bon)}
        sourceInitiale={(() => {
          const lines = lignesAEtiqueterDuBon(bon);
          const ctl = lines.reduce((n, x) => n + x.ctl, 0); const done = lines.reduce((n, x) => n + x.done, 0);
          return ctl > 0 ? 'ctl' : done > 0 ? 'done' : 'wait';
        })()}
        onClose={() => setEtiquettes(false)}
      />
    )}
    </>
  );
}

/** Une ligne connue du bon — le contrôle (−/saisie/+), l'écart, le prix avec son origine. */
function LigneConnue({ l, verrouille, etat, onControle, onSupprime }: {
  l: DeliveryNoteLine; verrouille: boolean; etat: string;
  /** Écrit le contrôle — `false` si le serveur l'a refusé. */
  onControle: (l: DeliveryNoteLine, checkedQty: number | null, unitPriceFine?: number | null) => Promise<boolean>;
  onSupprime: () => void;
}) {
  const [saisie, setSaisie] = useState(l.checkedQty == null ? '' : String(l.checkedQty));
  const [pa, setPa] = useState(l.unitPriceFine == null ? '' : euroFin(l.unitPriceFine));
  useEffect(() => { setSaisie(l.checkedQty == null ? '' : String(l.checkedQty)); }, [l.checkedQty]);
  useEffect(() => { setPa(l.unitPriceFine == null ? '' : euroFin(l.unitPriceFine)); }, [l.unitPriceFine]);

  // ⚠️ Une ligne dont tout est entré se VERROUILLE : ce qui est en stock ne se recompte pas ici.
  const lock = verrouille || (l.receivedQty > 0 && l.checkedQty != null && l.receivedQty >= l.checkedQty);
  const variance = l.checkedQty == null ? null : l.checkedQty - l.expectedQty;
  const reste = l.checkedQty == null ? 0 : Math.max(0, l.checkedQty - l.receivedQty);
  const { ps, psT } = origineDuPrix(l.priceSource);
  const canDel = l.receivedQty === 0 && etat !== 'received' && !verrouille;

  const validerSaisie = () => {
    const entered = saisie.trim();
    const v = entered === '' ? null : Number(entered);
    if (v !== null && (!Number.isInteger(v) || v < 0)) { showToast(t('Quantité contrôlée : un entier, ou vide pour « pas encore contrôlée ».'), 'warn'); return; }
    // ⚠️ Jamais SOUS le déjà-entré : ce qui est en stock ne se décompte pas au contrôle (maquette).
    const borne = v === null ? null : Math.max(l.receivedQty, v);
    // ⚠️ UN CONTRÔLE REFUSÉ NE RESTE PAS DANS LA CASE : le bon relu ne change pas, donc rien d'autre ne la remettrait
    //    à la vérité — elle afficherait une quantité que le serveur n'a jamais enregistrée.
    if (borne !== l.checkedQty) void onControle(l, borne).then((ok) => { if (!ok) setSaisie(l.checkedQty == null ? '' : String(l.checkedQty)); });
    else if (borne !== v) setSaisie(String(borne));
  };
  const validerPa = () => {
    const fin = finDepuisSaisie(pa);
    if (fin === undefined) { showToast(t('Prix d’achat HT attendu, par exemple « 28,40 » — vide = à valoriser.'), 'warn'); return; }
    // Même raison : un prix refusé ne reste pas affiché comme s'il était retenu.
    if (fin !== l.unitPriceFine) void onControle(l, l.checkedQty, fin).then((ok) => { if (!ok) setPa(l.unitPriceFine == null ? '' : euroFin(l.unitPriceFine)); });
  };
  const bouton = { width: 24, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 } as const;
  const ctlBd = l.checkedQty == null || (variance != null && variance !== 0) ? 'var(--amber)' : 'var(--ink-200)';
  // ⚠️ CHAQUE CONTRÔLE NOMME SA LIGNE : « Quantité contrôlée » se répète sur chaque déclinaison du bon.
  const ligne = [l.name ?? l.gencode, l.color, l.size].filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '7px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px', background: lock ? 'var(--ink-50)' : 'transparent' }}>
      <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
        {lock && <Icon name="lock" size={12} color="var(--ink-400)" />}
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name ?? l.gencode}{l.color ? ` · ${l.color}` : ''}</span>
          <span style={{ display: 'block', fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)' }}>{l.gencode}</span>
        </span>
      </span>
      <span style={{ textAlign: 'center', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)' }}>{l.size ?? '—'}</span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{l.expectedQty}</span>
      <span style={{ display: 'flex', justifyContent: 'center' }}>
        {lock ? (
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{l.checkedQty ?? '—'}</span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <button type="button" aria-label={t('Retirer une pièce contrôlée — {ligne}', { ligne })} style={bouton}
              onClick={() => void onControle(l, Math.max(l.receivedQty, (l.checkedQty ?? 0) - 1))}>−</button>
            <input
              value={saisie} onChange={(e) => setSaisie(e.target.value)} onBlur={validerSaisie}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              aria-label={t('Quantité contrôlée — {ligne}', { ligne })} placeholder="—"
              style={{ width: 44, height: 30, textAlign: 'center', border: `1px solid ${ctlBd}`, borderRadius: 'var(--r-xs)', background: l.checkedQty == null ? 'var(--amber-bg)' : 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', outline: 'none' }}
            />
            <button type="button" aria-label={t('Ajouter une pièce contrôlée — {ligne}', { ligne })} style={bouton}
              onClick={() => void onControle(l, (l.checkedQty ?? 0) + 1)}>+</button>
          </span>
        )}
      </span>
      <span style={{ textAlign: 'center' }}>
        {variance != null && variance !== 0 ? <Ecart n={variance} />
          : variance === 0 ? <span style={{ display: 'inline-flex', alignItems: 'center', height: 19, padding: '0 6px', borderRadius: 'var(--r-xs)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '10.5px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>0</span>
            : <span style={{ color: 'var(--ink-300)' }}>—</span>}
      </span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: l.receivedQty > 0 ? 'var(--teal-800)' : 'var(--ink-400)' }}>{l.receivedQty}</span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: reste > 0 ? 'var(--ink-900)' : 'var(--ink-300)' }}>{reste}</span>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
        {lock ? (
          <span style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{euroFin(l.unitPriceFine)}</span>
        ) : (
          <input
            value={pa} onChange={(e) => setPa(e.target.value)} onBlur={validerPa}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            aria-label={t('{v} — {ligne}', { v: fieldLabel('purchasePriceExclTax'), ligne })} placeholder="—"
            style={{ width: 56, height: 30, textAlign: 'right', border: `1px solid ${l.unitPriceFine == null ? 'var(--rose-fg)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '11.5px', fontWeight: 600, fontFamily: MONO, outline: 'none', fontVariantNumeric: 'tabular-nums' }}
          />
        )}
        <span title={psT} style={{ height: 17, padding: '0 5px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: '8.5px', fontWeight: 800, letterSpacing: '.03em', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'help' }}>{ps}</span>
        {canDel && (
          <button type="button" aria-label={t('Supprimer la ligne du BL — {ligne}', { ligne })} title={t('Supprimer la ligne — rien n’a été entré en stock')}
            onClick={onSupprime}
            style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}
          ><Icon name="trash" size={13} /></button>
        )}
      </span>
    </div>
  );
}

// ── « CORRIGER LE STOCK » — le BL reste figé, chaque écart devient un mouvement signé (acFix, D19) ──
function CorrectionModal({ bon, onClose, onEcrit }: { bon: DeliveryNoteDetail; onClose: () => void; onEcrit: (b: DeliveryNoteDetail) => void }) {
  // ⚠️ La base préremplie est la VRAIE : entré + corrections antérieures (`fixedDelta` du serveur) —
  //    préremplir « entré » seul ferait retaper la même cible après une première correction.
  const lines = useMemo(() => bon.lines.filter((l) => l.known && l.receivedQty > 0)
    .map((l) => ({ l, base: l.receivedQty + l.fixedDelta })), [bon]);
  const [values, setValeurs] = useState<Record<string, string>>(() => Object.fromEntries(lines.map((x) => [x.l.gencode, String(x.base)])));
  const [filtre, setFiltre] = useState('');
  const [motif, setMotif] = useState<StockCorrectionReason | ''>('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [ecrit, setEcrit] = useState(false);
  const champRef = useRef<HTMLInputElement>(null);
  useEffect(() => { champRef.current?.focus(); }, []);

  const rows = lines.map(({ l, base }) => {
    const v = values[l.gencode] ?? String(base);
    const nv = Number.parseInt(v, 10);
    const delta = (Number.isNaN(nv) ? base : nv) - base;
    return { l, base, v, delta };
  });
  const fq = filtre.trim().toLowerCase();
  // La recherche filtre, mais une ligne MODIFIÉE reste visible : on ne perd pas une correction en tapant.
  const visibles = fq
    ? rows.filter((r) => r.delta !== 0 || fq.split(/\s+/).every((w) => `${r.l.gencode} ${r.l.name ?? ''} ${r.l.size ?? ''}`.toLowerCase().includes(w)))
    : rows;
  const modifiees = rows.filter((r) => r.delta !== 0);

  const write = async () => {
    if (modifiees.length === 0) { setErreur(t('Aucune quantité modifiée — corrigez au moins une ligne.')); return; }
    if (!motif) { setErreur(t('Choisissez un motif : la correction doit être justifiée.')); return; }
    if (ecrit) return;
    setEcrit(true);
    try {
      // ⚠️ LE LIBELLÉ PART DANS LA NOTE DU MOUVEMENT, un texte humain : il s'écrit dans la langue de qui corrige,
      //    comme une note saisie — le code fermé, lui, voyage dans `reason`.
      const label = t(MOTIFS_CORRECTION.find((m) => m.v === motif)!.l);
      const r = await api.correctDelivery(bon.number, modifiees.map((x) => ({ gencode: x.l.gencode, correctedTo: x.base + x.delta })), motif, label);
      showToast(t('{n} mouvement de correction écrit · {number} — motif « {label} »', { n: r.movements, number: bon.number, label }));
      onEcrit(r.bon);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
      setEcrit(false);
    }
  };

  const GRID_FIX = 'minmax(0,1fr) 54px 82px 96px 64px';
  return (
    <Modal
      open onClose={onClose} title={t('Corriger le stock — {number}', { number: bon.number })} width="lg" icon="edit"
      subtitle={t('Le BL reste figé : chaque écart devient un mouvement signé, motif obligatoire')}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => void write()} aria-disabled={ecrit || undefined}
            style={{
              height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: '13px', fontWeight: 700,
              background: modifiees.length > 0 && motif ? 'var(--teal-500)' : 'var(--ink-200)',
              color: modifiees.length > 0 && motif ? '#fff' : 'var(--ink-400)',
              cursor: ecrit ? 'progress' : 'pointer',
            }}
          >{t('Écrire la correction ({n})', { n: modifiees.length })}</button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', border: '1.5px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-100)' }}>
            <Icon name="search" size={15} color="var(--teal-800)" />
            <input ref={champRef} value={filtre} onChange={(e) => setFiltre(e.target.value)}
              placeholder={t('Scannez ou tapez — code, nom d’article, taille')}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13px', color: 'var(--ink-900)', outline: 'none' }} />
          </div>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{t('{v} / {n} ligne', { v: visibles.length, n: rows.length })}</span>
        </div>
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID_FIX, gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            <span>{t('Article')}</span><span style={{ textAlign: 'center' }}>{fieldLabel('size')}</span><span style={{ textAlign: 'right' }}>{t('Entré')}</span><span style={{ textAlign: 'center' }}>{t('Corrigé à')}</span><span style={{ textAlign: 'right' }}>{t('Delta')}</span>
          </div>
          {visibles.map((r) => (
            <div key={r.l.gencode} style={{ display: 'grid', gridTemplateColumns: GRID_FIX, gap: 8, padding: '8px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.l.name ?? r.l.gencode}</span>
                <span style={{ fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.l.gencode}</span>
              </span>
              <span style={{ textAlign: 'center', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)' }}>{r.l.size ?? '—'}</span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{r.base}</span>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                <input
                  value={r.v} inputMode="numeric" aria-label={t('Quantité corrigée — {v}', { v: r.l.name ?? r.l.gencode })}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => { setErreur(null); setValeurs((v) => ({ ...v, [r.l.gencode]: e.target.value.replace(/[^0-9]/g, '') })); }}
                  style={{ width: 64, height: 32, textAlign: 'center', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', fontWeight: 700, outline: 'none', fontVariantNumeric: 'tabular-nums' }}
                />
              </span>
              <span style={{ textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: r.delta === 0 ? 'var(--ink-300)' : r.delta > 0 ? 'var(--teal-800)' : 'var(--rose-fg)' }}>
                {r.delta === 0 ? '—' : r.delta > 0 ? `+${r.delta}` : r.delta}
              </span>
            </div>
          ))}
          {visibles.length === 0 && (
            <div style={{ padding: '18px 14px', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-400)' }}>{t('Aucune ligne ne correspond — vérifiez le code ou effacez la recherche.')}</div>
          )}
        </div>
        <label>
          <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>{t('Motif de correction — référentiel')}</span>
          <select value={motif} onChange={(e) => { setMotif(e.target.value as StockCorrectionReason | ''); setErreur(null); }}
            style={{ width: '100%', height: 38, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', boxSizing: 'border-box' }}>
            <option value="">{t('Choisir un motif…')}</option>
            {MOTIFS_CORRECTION.map((m) => <option key={m.v} value={m.v}>{t(m.l)}</option>)}
          </select>
        </label>
        {erreur && (
          <div role="alert" style={{ padding: '9px 13px', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-bg)', fontSize: '12px', fontWeight: 600, color: 'var(--rose-fg)' }}>{erreur}</div>
        )}
        <div style={{ padding: '11px 13px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.55 }}>
          {t('Rien ne s’efface : la ligne du BL garde son historique, la correction s’écrit comme un mouvement « Correction réception {number} » visible dans Stock → Mouvements et sur la fiche article.', { number: bon.number })}</div>
      </div>
    </Modal>
  );
}
